import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  forwardRef,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  AsYouTypeFormatter,
  PhoneNumberFormat,
  PhoneNumberUtil,
} from 'google-libphonenumber';

@Component({
    selector: 'phone-input',
    imports: [CommonModule, ReactiveFormsModule, TranslateModule],
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true,
        },
    ]
})
export class PhoneInputComponent
  implements ControlValueAccessor, OnInit, Validator
{
  @Input() defaultCountry = 'MX';
  @Input() countries: { code: string; dialCode: string; flag: string; name?: string }[] = [];

  phoneUtil = PhoneNumberUtil.getInstance();

  phone = new FormControl('');
  searchControl = new FormControl('');
  selectedCountry: string = this.defaultCountry;

  dropdownOpen = false;

  /** Reference to the outer NgControl (formControlName="phone") so we can
   *  reflect its real validity/touched state, since the internal `phone`
   *  FormControl has no validators of its own. Resolved lazily via the
   *  Injector to avoid a circular DI error with NG_VALUE_ACCESSOR. */
  private ngControl: NgControl | null = null;

  /** Live number formatter (e.g. "33 1234 5678"), similar to Stripe's phone
   *  field, re-created whenever the selected country changes. */
  private asYouTypeFormatter!: AsYouTypeFormatter;
  private hasUserSelectedCountry = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private injector: Injector,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    this.selectedCountry = this.resolveInitialCountry();
    this.asYouTypeFormatter = new AsYouTypeFormatter(this.selectedCountry);
    void this.hydrateCountryFromGeoLookup();
  }

  onBlur(): void {
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.dropdownOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.dropdownOpen = false;
      this.searchControl.setValue('', { emitEvent: false });
      this.onTouched();
    }
  }

  get showError(): boolean {
    return !!this.ngControl && !!this.ngControl.invalid && !!this.ngControl.touched;
  }

  /** Reformats the number as the user types (Stripe-style live formatting),
   *  keeping the caret positioned after the same digit it followed before. */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsBeforeCaret = input.value
      .slice(0, input.selectionStart ?? input.value.length)
      .replace(/\D+/g, '').length;
    const digits = input.value.replace(/\D+/g, '').slice(0, 15);

    const formatted = this.formatAsYouType(digits);
    this.phone.setValue(formatted, { emitEvent: false });

    const caret = this.digitCountToIndex(formatted, digitsBeforeCaret);
    queueMicrotask(() => input.setSelectionRange(caret, caret));

    this.updateValue();
  }

  private formatAsYouType(digits: string): string {
    this.asYouTypeFormatter.clear();
    let formatted = '';
    for (const digit of digits) {
      formatted = this.asYouTypeFormatter.inputDigit(digit);
    }
    return formatted;
  }

  private digitCountToIndex(formatted: string, digitCount: number): number {
    if (digitCount <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        seen++;
        if (seen === digitCount) return i + 1;
      }
    }
    return formatted.length;
  }

  writeValue(value: string): void {
    if (!value) {
      this.phone.setValue('', { emitEvent: false });
      return;
    }

    try {
      const parsed = this.phoneUtil.parse(value);
      const regionCode = this.phoneUtil.getRegionCodeForNumber(parsed);
      this.selectedCountry = regionCode || this.defaultCountry;
      this.asYouTypeFormatter = new AsYouTypeFormatter(this.selectedCountry);
      const nationalNumber = parsed.getNationalNumber();
      const digits = nationalNumber ? nationalNumber.toString() : '';
      this.phone.setValue(this.formatAsYouType(digits), { emitEvent: false });
    } catch {
      this.phone.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) this.phone.disable({ emitEvent: false });
    else this.phone.enable({ emitEvent: false });
  }

  validate(): ValidationErrors | null {
    const value = this.phone.value;
    if (!value) return { required: true };

    try {
      const dialCode =
        this.countries.find((c) => c.code === this.selectedCountry)?.dialCode ||
        '';
      const parsed = this.phoneUtil.parseAndKeepRawInput(
        dialCode + value,
        this.selectedCountry
      );
      return this.phoneUtil.isValidNumber(parsed)
        ? null
        : { invalidNumber: true };
    } catch {
      return { invalidNumber: true };
    }
  }

  updateValue(): void {
    try {
      const dialCode =
        this.countries.find((c) => c.code === this.selectedCountry)?.dialCode ||
        '';
      const fullNumber = dialCode + this.phone.value;
      const parsed = this.phoneUtil.parseAndKeepRawInput(
        fullNumber,
        this.selectedCountry
      );
      if (this.phoneUtil.isValidNumber(parsed)) {
        const formatted = this.phoneUtil.format(
          parsed,
          PhoneNumberFormat.INTERNATIONAL
        );
        this.onChange(formatted);
      } else {
        this.onChange(null);
      }
    } catch {
      this.onChange(null);
    }
  }

  filteredCountries() {
    const term = this.searchControl.value?.toLowerCase() || '';
    return this.countries.filter((c) => {
      const countryName = (c.name ?? '').toLowerCase();
      return (
        c.code.toLowerCase().includes(term) ||
        c.dialCode.toLowerCase().includes(term) ||
        countryName.includes(term) ||
        c.flag.toLowerCase().includes(term)
      );
    });
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  selectCountry(code: string) {
    this.hasUserSelectedCountry = true;
    this.selectedCountry = code;
    this.asYouTypeFormatter = new AsYouTypeFormatter(code);
    this.dropdownOpen = false;
    this.searchControl.setValue('');

    // Re-format the digits already typed for the newly selected country.
    const digits = (this.phone.value ?? '').replace(/\D+/g, '');
    this.phone.setValue(this.formatAsYouType(digits), { emitEvent: false });

    this.updateValue();
  }

  getSelectedCountryLabel(): string {
    const country = this.countries.find((c) => c.code === this.selectedCountry);
    return country
      ? `${country.flag} ${country.code} ${country.dialCode}`
      : 'Selecciona país';
  }

  getSelectedDialCodeLabel(): string {
    const country = this.countries.find((c) => c.code === this.selectedCountry);
    return country ? `${country.dialCode}` : 'Selecciona país';
  }

  private resolveInitialCountry(): string {
    const detectedCountry = this.detectBrowserCountry();
    if (detectedCountry) {
      return detectedCountry;
    }

    return this.isSupportedCountryCode(this.defaultCountry)
      ? this.defaultCountry
      : 'MX';
  }

  private detectBrowserCountry(): string | null {
    if (typeof navigator === 'undefined') {
      return null;
    }

    const locales = [
      ...(navigator.languages ?? []),
      navigator.language,
    ].filter((locale): locale is string => !!locale);

    for (const locale of locales) {
      const region = this.extractRegionFromLocale(locale);
      if (region && this.isSupportedCountryCode(region)) {
        return region;
      }
    }

    return null;
  }

  private async hydrateCountryFromGeoLookup(): Promise<void> {
    if (typeof fetch === 'undefined') {
      return;
    }

    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        return;
      }

      const location = (await response.json()) as { country?: string };
      const detectedCountry = location.country?.toUpperCase();

      if (!this.isSupportedCountryCode(detectedCountry)) {
        return;
      }

      if (this.hasUserSelectedCountry || !!this.phone.value) {
        return;
      }

      this.selectedCountry = detectedCountry!;
      this.asYouTypeFormatter = new AsYouTypeFormatter(this.selectedCountry);
    } catch {
      // Keep the locale/default-based fallback when geo lookup fails.
    }
  }

  private extractRegionFromLocale(locale: string): string | null {
    try {
      const intlLocale = new Intl.Locale(locale);
      const region = intlLocale.region?.toUpperCase();
      if (region) {
        return region;
      }
    } catch {
      // Fallback below for browsers/locales that Intl.Locale cannot parse.
    }

    const parts = locale.split(/[-_]/).map((part) => part.trim());
    const regionPart = parts.slice(1).find((part) => /^[A-Za-z]{2}$/.test(part));

    return regionPart ? regionPart.toUpperCase() : null;
  }

  private isSupportedCountryCode(code: string | null | undefined): boolean {
    if (!code) {
      return false;
    }

    return this.countries.some((country) => country.code === code.toUpperCase());
  }
}
